// @givmo/app-admin — the test-connection browser round trip (AS-WEB6).
//
// ══ THE SHAPE OF THE PROBLEM ══════════════════════════════════════════════════════════════════
// Running a test connection sends the Owner's browser to their identity provider and back. Two
// things have to survive that trip: the `test_id` (so the outcome can be read afterwards) and some
// signal that the browser DID come back. Neither can ride the URL — the backend's redirect
// deliberately carries no verdict and no id, because putting a company's identity-provider
// diagnostic into browser history and every `Referer` on the way is not a trade worth making for
// convenience.
//
// So there are two return paths and they behave differently, which is the whole reason this module
// exists rather than a `useState`:
//
//   • NEW TAB (the ordinary case). The settings page is still mounted and still polling
//     `GET …/test-connection/{test_id}`, so the outcome arrives on its own. The landing page's only
//     jobs are to close itself and — best effort — nudge the opener to poll immediately.
//   • SAME TAB (pop-up blocked, or a provider that forces a top-level navigation). The React tree
//     was destroyed. The `test_id` has to come back from somewhere, and `sessionStorage` is that
//     somewhere: it is per-tab and survives navigation within the tab.
//
// ⚠ `sessionStorage` IS COPIED INTO A NEW TAB, NOT SHARED WITH IT. A tab opened from this one starts
// with a snapshot, and its writes never reach us. That is exactly why the new-tab path cannot use
// storage to report an outcome and uses the poll instead — a design that "worked" in a same-tab test
// and silently did nothing in the real flow is the failure this note exists to prevent.
//
// ══ THERE IS NO OPENER, AND THEREFORE NO MESSAGE CHANNEL ══════════════════════════════════════
// An earlier draft opened the ceremony WITHOUT `noopener` so the landing page could `postMessage`
// its opener to make the poll fire immediately. That trade is not available: the broker page and the
// employer's identity provider (and anything they bounce through) sit in that same tab and would
// each hold `window.opener` on the Owner's authenticated portal — reverse tabnabbing, one
// `opener.location.replace(…)` away from a credential-shaped page, with no
// `Cross-Origin-Opener-Policy` on the admin hosting to blunt it. The window is opened `noopener`, so
// the handle does not exist and the nudge cannot be built.
//
// Nothing is lost but latency: the POLL WAS ALWAYS THE CONTRACT (the nudge was documented as "a
// hint, never a verdict"), the outcome is read from the Owner-gated endpoint either way, and the
// cost of losing the hint is at most one 3-second interval — with "Check again" available
// throughout. Do not re-introduce a message channel here without also shipping COOP.

/**
 * The route the backend redirects a finished test connection to, in the employer portal.
 *
 * ⚠ This exact string, joined to each environment's admin origin, is what the operator registers in
 * `EMPLOYER_SSO_ALLOWED_REDIRECT_URIS`; the backend matches it EXACTLY and otherwise refuses
 * `SSO_REDIRECT_URI_NOT_ALLOWED`. Changing the path is therefore an operator change in three
 * environments, not a refactor — treat it like the testid contract. The company console registers
 * its own beside it (`security-host.tsx`: each host names its callback).
 */
export const SSO_TEST_CALLBACK_PATH = '/settings/security/sso/test-callback';

/**
 * Where the landing page sends the Owner back to — the Security tab, deep-linked through the
 * settings route's `?tab=` search param.
 *
 * The tab id is spelled out rather than imported from `settings-security-tab.tsx`, which would close
 * an import cycle (that module → `sso-section` → this one). `sso-test-session.test.ts` pins the two
 * against each other instead, so a rename fails a test rather than silently landing on the wrong tab.
 */
export const SSO_SETTINGS_RETURN_PATH = '/settings';
export const SSO_SETTINGS_RETURN_TAB = 'security';

/**
 * The redirect URI for this deployment: the running origin plus the host's callback path (the
 * portal's above unless a host names its own, `security-host.tsx`).
 *
 * Derived from `location.origin` rather than from a `VITE_*` constant deliberately. The value has to
 * be byte-identical to what the operator registered for THIS origin, and an environment variable is
 * a second place for that to be wrong — a staging build carrying a prod origin would send the
 * Owner's browser to another environment, and a mismatch would surface as a refusal nobody could
 * explain. The running origin cannot disagree with itself.
 */
export function ssoTestRedirectUri(
  callbackPath: string = SSO_TEST_CALLBACK_PATH,
  origin: string = globalThis.location?.origin ?? '',
): string {
  return `${origin}${callbackPath}`;
}

/** The `sessionStorage` key. Namespaced so nothing else in the tab can collide with it. */
export const SSO_TEST_STORAGE_KEY = 'givmo.admin.sso-test';

/**
 * How long a remembered test may be resumed for.
 *
 * The backend's ceremony expires on its own, so a stale id is harmless — it reads as `expired`. The
 * bound is about the SCREEN: rehydrating an hours-old attempt would open the settings page onto a
 * failure report for something the Owner had long since abandoned. Fifteen minutes is comfortably
 * longer than any real identity-provider sign-in and shorter than "later today".
 */
export const SSO_TEST_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * What is remembered across the trip: THE TEST ID AND WHEN IT STARTED, and nothing else.
 *
 * ⚠ An earlier draft also stamped the callback's outcome and the raw `?error=` code here. Nothing
 * ever read them back — the outcome comes from the Owner-gated endpoint, which is the only thing
 * entitled to state it — so they were write-only state that looked like a source of truth. Storing
 * a verdict nobody consults is how the next session ends up rendering it.
 */
export interface RememberedSsoTest {
  testId: string;
  startedAt: number;
}

/**
 * Every storage access goes through this.
 *
 * `sessionStorage` THROWS rather than returning null in a Safari private window and under some
 * enterprise policies, and this feature must degrade to "the new-tab path still works" rather than
 * taking the Security tab down with it. There is nothing here worth an error boundary.
 */
function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Record a started test so a SAME-TAB return can pick it up again. */
export function rememberSsoTest(testId: string, now: number = Date.now()): void {
  try {
    storage()?.setItem(SSO_TEST_STORAGE_KEY, JSON.stringify({ testId, startedAt: now }));
  } catch {
    // Quota or a disabled store. The new-tab path is unaffected; nothing else depends on this.
  }
}

/**
 * The remembered test, or `null` — for an absent entry, unparseable JSON, a missing/blank id, or one
 * older than {@link SSO_TEST_MAX_AGE_MS}.
 *
 * Tolerant on purpose: this reads a value another page wrote into a store the user can edit, so
 * every field is checked rather than assumed. A malformed entry returns `null` and is left in place
 * — clearing it here would make a read function write, and the next `rememberSsoTest` overwrites it
 * anyway.
 */
export function recallSsoTest(now: number = Date.now()): RememberedSsoTest | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(SSO_TEST_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const entry = parsed as Record<string, unknown>;
  const testId = typeof entry.testId === 'string' ? entry.testId.trim() : '';
  const startedAt = typeof entry.startedAt === 'number' ? entry.startedAt : NaN;
  if (testId.length === 0 || !Number.isFinite(startedAt)) return null;
  if (now - startedAt > SSO_TEST_MAX_AGE_MS) return null;

  return { testId, startedAt };
}

/** Drop the remembered test. Called when the Owner starts another one or leaves the outcome behind. */
export function forgetSsoTest(): void {
  try {
    storage()?.removeItem(SSO_TEST_STORAGE_KEY);
  } catch {
    // See `rememberSsoTest`.
  }
}
